import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { Check } from "lucide-react";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "Pricing — Websites, Software, Hosting & Marketing — CoderLity" },
      { name: "description", content: "Transparent pricing for websites, SaaS, ERP, POS, hosting, marketing and maintenance. Plans from $9/mo to enterprise." },
      { property: "og:title", content: "Pricing — CoderLity" },
      { property: "og:description", content: "Simple pricing for websites, software, hosting and marketing." },
      { property: "og:url", content: "/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/pricing" }],
  }),
});

const plans = [
  {
    name: "Starter",
    price: "$49",
    period: "/month",
    tagline: "Best for small businesses just getting online.",
    features: ["1 website", "Managed hosting", "Free SSL + backups", "Email support", "Monthly reports"],
    cta: "Start Starter",
  },
  {
    name: "Growth",
    price: "$199",
    period: "/month",
    tagline: "Websites + marketing for scaling teams.",
    highlighted: true,
    features: ["Everything in Starter", "SEO + content (4 posts/mo)", "Google & Meta ads mgmt", "CRO + A/B testing", "Priority support"],
    cta: "Start Growth",
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "For companies with custom software and SLAs.",
    features: ["Custom ERP/POS/CRM", "Dedicated infra + SLA", "Dedicated engineer", "White-glove onboarding", "24/7 support"],
    cta: "Talk to sales",
  },
];

const addons = [
  { name: "Additional website", price: "$29/mo" },
  { name: "Extra 100GB storage", price: "$10/mo" },
  { name: "Additional ad-spend channel", price: "$300/mo" },
  { name: "Custom integration", price: "from $1,500" },
  { name: "Priority SLA (4-hour)", price: "$500/mo" },
];

function PricingPage() {
  return (
    <PageShell>
      <PageHero eyebrow="Pricing" title="Simple, transparent pricing" sub="Pick a plan, cancel anytime, upgrade or downgrade whenever." />

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-6 md:grid-cols-3">
          {plans.map((p) => (
            <div key={p.name} className={`rounded-2xl border p-8 ${p.highlighted ? "border-primary bg-primary/5 shadow-glow" : "border-border bg-card"}`}>
              <div className="text-sm font-semibold text-primary">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold">{p.price}</span>
                <span className="text-muted-foreground">{p.period}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
              <ul className="mt-6 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to="/contact" className={`mt-8 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold ${p.highlighted ? "bg-gradient-brand text-white shadow-glow" : "border border-border hover:bg-muted"}`}>
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">Add-ons</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {addons.map((a) => (
              <div key={a.name} className="flex items-center justify-between p-4 text-sm">
                <span>{a.name}</span>
                <span className="font-semibold">{a.price}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </PageShell>
  );
}
